<template>
  <transition name="slideup" mode="out-in">
    <div v-if="isOpen" :class="wrapperClass">
      <span
        class="absolute top-4 right-2 cursor-pointer text-grey-500"
        @click="close()"
        v-if="closable"
        ><close-icon class="h-4 w-4 cursor-pointer"></close-icon
      ></span>
      <div class="flex items-center gap-2 p-4">
        <span v-if="showIcon" :style="bgColor" class="inline-flex justify-center items-center w-7 h-7 rounded-full">
          <component :is="icon" :class="iconClass"></component>
        </span>
        <p class="text-sm text-grey-700">{{ text }}</p>
      </div>
      <div v-if="actions.length" :class="actionClass">
        <button
          v-for="(action, index) in actions"
          :key="index"
          class="font-bold text-primary text-sm"
          @click="action.callback"
        >
          {{ action.label }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { twJoin } from 'tailwind-merge';
import CloseIcon from '../assets/close-icon-new.svg';
import SuccessIcon from '../assets/icons/check-circle.svg';
import WarningIcon from '../assets/icons/exclamation-circle.svg';
import InfoIcon from '../assets/icons/info-circle.svg';
import DangerIcon from '../assets/icons/times-circle.svg';

export default {
  name: 'GdBanner',
  props: {
    text: { type: String, required: true },
    actions: { type: Array, default: () => [] },
    variant: { type: String, default: 'info' }, // info, success, warning, danger
    closable: { type: Boolean, default: true },
    align: { type: String, default: 'column' }, // column, row
    showIcon: { type: Boolean, default: true },
    position: { type: String, default: 'bottom-right' }, //top-right, top-left, bottom-right, bottom-left, relative
    expand: { type: Boolean, default: false }
  },
  components: { CloseIcon, InfoIcon, SuccessIcon, WarningIcon, DangerIcon },
  data() {
    return { isOpen: true };
  },
  computed: {
    wrapperClass() {
      const baseClass =
        'rounded-md shadow-md flex bg-white border border-grey-300';
      const widthClass = this.expand ? 'w-full' : 'w-96';
      const directionClass = {
        column: 'flex-col pr-4',
        row: 'flex-row justify-between gap-4 items-center pr-8'
      };
      const positionClass = {
        'top-left': 'fixed top-4 left-4',
        'top-right': 'fixed top-4 right-4',
        'bottom-left': 'fixed bottom-4 left-4',
        'bottom-right': 'fixed bottom-4 right-4',
        relative: 'relative'
      };
      return twJoin(
        baseClass,
        widthClass,
        directionClass[this.align],
        positionClass[this.position]
      );
    },
    actionClass() {
      const baseClass = 'flex gap-2 items-center p-4 pr-0';
      const directionClass = this.align === 'column' ? 'justify-end' : '';
      return twJoin(baseClass, directionClass);
    },
    iconClass() {
      const baseClass = 'w-5 h-5 fill-current flex-shrink-0';
      const variantClass = {
        info: 'text-blue',
        success: 'text-success',
        warning: 'text-warning',
        danger: 'text-danger'
      };
      return twJoin(baseClass, variantClass[this.variant]);
    },
    icon() {
      const variantIcon = {
        info: InfoIcon,
        success: SuccessIcon,
        warning: WarningIcon,
        danger: DangerIcon
      };
      return variantIcon[this.variant];
    },
    bgColor() {
      switch (this.variant) {
        case 'info':
          return `background-color: color-mix(in srgb, rgb(48, 139, 229) 20%, white)`;
        case 'success':
          return `background-color: color-mix(in srgb, var(--brand-success) 20%, white)`;
        case 'warning':
          return `background-color: color-mix(in srgb, var(--brand-warning) 20%, white)`;
        case 'danger':
          return `background-color: color-mix(in srgb, var(--brand-danger) 20%, white)`;
      }
    }
  },
  methods: {
    close() {
      this.isOpen = false;
    }
  }
};
</script>
